import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { WeekGrid } from '../components/BoardGrid.jsx'
import WallDay, { crewRows } from '../components/WallBoard.jsx'
import ContextMenu, { ContextMenuItem, ContextMenuLabel } from '../components/ContextMenu.jsx'
import Icon from '../components/Icon.jsx'
import { addDays, formatDate, weekdayName, formatShort } from '../lib/dates.js'
import { nameOf } from '../lib/modes.js'
import { applyBranding } from '../lib/theme.js'
import './whiteboard.css'

// READ-ONLY, for everyone, always — including owners.
//
// Note what this file does not import: CellEditor. There is no code path from this screen to
// a write, which is a stronger guarantee than a disabled button. Someone who wants to move a
// crew walks to the board.

const REFRESH_MS = 30_000
// How often a paired screen says it is still alive. Coarse on purpose: the owner's
// Displays page asks "has this gone dark", which a minute answers as well as a second
// does, and this write happens on every wall in the building forever.
const HEARTBEAT_MS = 60_000
const IDLE_RESET_MS = 5 * 60_000
const STICKY_KEY = 'fgs-wb-sticky-page'
// Two columns is what usually gets a whole shop onto one screen in the day view: a day's
// table is Staff plus three job slots, so it is narrow enough to sit beside a copy of itself.
// The week view gets one, because its table is already Staff plus a column per working day —
// two of those side by side is six inches of name column and nothing legible in the cells.
const COLUMNS_BY_VIEW = { day: 2, week: 1 }
// How far the board may shrink to get everyone onto one screen. Below this it stops being
// readable from across a shop, which is the entire job — better to turn a page than to put
// up something nobody can read.
const MIN_WALL_SCALE = 0.6
// Hysteresis on the decision to page. `needed` is measured constants times a row count, so a
// roster that wants almost exactly the floor sits right on the boundary — and a board that
// flips between one screen and two is worse than either answer. Coming back to one screen
// therefore needs real headroom, not a tie.
const PAGE_HYSTERESIS = 0.04
// Starting guesses, and the fallback for anything the probe cannot find. Sized for the day
// view's own type scale so the first paint is close before anything is measured.
const DEFAULT_METRICS = {
  day: { row: 46, crew: 62, empty: 40, head: 30 },
  week: { row: 52, crew: 58, head: 34, entry: 28 },
}
// The synthetic ids the measurement probe's one crew and one person are built from. Prefixed
// so they cannot collide with a real row and be mistaken for one.
const PROBE_ID = '__wall-probe'

/**
 * Pinning the wall to one page. A shop with more crews than fit on a screen may still want
 * a particular TV to show only its own bay's page rather than cycling past it — the point
 * of a wall board is that you glance up and it is already showing what you need.
 *
 * Stored as a 1-based page number because that is what is on screen and in the menu; the
 * off-by-one lives here rather than in the reader's head. Persisted so the nightly watchdog
 * reload does not quietly start it rotating again at 3am.
 */
function useStickyPage() {
  const [sticky, setSticky] = useState(() => {
    // Guarded like the write below, and for the same reason it names: a locked-down kiosk
    // profile throws on ACCESS, reads included. Unguarded, that threw during render — so
    // the one screen nobody can reach to debug showed a blank wall, over a page number.
    try {
      const n = Number(localStorage.getItem(STICKY_KEY))
      return Number.isInteger(n) && n > 0 ? n : null
    } catch {
      return null
    }
  })
  const choose = (n) => {
    setSticky(n)
    try {
      if (n) localStorage.setItem(STICKY_KEY, String(n))
      else localStorage.removeItem(STICKY_KEY)
    } catch {
      /* private browsing or a locked-down kiosk profile — it just won't outlive this load */
    }
  }
  return [sticky, choose]
}

export default function Whiteboard() {
  const [date, setDate] = useState(null)
  const [view, setView] = useState('day')
  const [page, setPage] = useState(0)
  const [now, setNow] = useState(() => new Date())
  const [stickyPage, chooseStickyPage] = useStickyPage()
  const [ctxMenu, setCtxMenu] = useState(null) // { x, y } or null
  const idleTimer = useRef(null)
  const mountedAt = useRef(Date.now())
  const bodyRef = useRef(null)

  const { data, isLoading, error, dataUpdatedAt } = useQuery({
    queryKey: ['whiteboard', date, view],
    queryFn: () => api.get(`/api/whiteboard?view=${view}${date ? `&date=${date}` : ''}`),
    refetchInterval: REFRESH_MS,
    // A dropped network holds the last good render rather than blanking the wall.
    placeholderData: (prev) => prev,
    retry: 3,
  })

  // Separate from the board query so the boot screen can be branded while the heavy one is
  // still in flight. Cheap, cached, and it is the same payload the app shell already uses.
  const { data: brandingOnly } = useQuery({
    queryKey: ['branding'],
    queryFn: () => api.get('/api/branding'),
    staleTime: 5 * 60_000,
  })
  const branding = data?.branding || brandingOnly

  const display = data?.display
  const today = data?.today

  useEffect(() => {
    if (data?.branding) applyBranding(data.branding)
  }, [data?.branding])

  useEffect(() => {
    if (!date && today) setDate(today)
  }, [today, date])

  useEffect(() => {
    if (display?.defaultView) setView(display.defaultView)
  }, [display?.defaultView])

  // The only write a wall TV makes, and the only thing that fills in Last seen on the
  // owner's Displays page. The endpoint, its allowlist entry and the last_seen columns all
  // shipped without this call, so every screen read as never seen however long it had been
  // running — including one working perfectly on a wall.
  //
  // Gated on `display`: somebody signed in and browsing this page is not a screen, and the
  // endpoint would rightly refuse them. Failures are swallowed because nothing about a
  // missed heartbeat should disturb what is on the wall.
  useEffect(() => {
    if (!display?.id) return
    const beat = () => { api.post('/api/display/heartbeat').catch(() => {}) }
    beat()
    const t = setInterval(beat, HEARTBEAT_MS)
    return () => clearInterval(t)
  }, [display?.id])

  // Clock for the "updated HH:MM" stamp and the night-dim window.
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000)
    return () => clearInterval(t)
  }, [])

  // Keep the panel awake. Not supported on Tizen/webOS — the install runbook covers turning
  // the TV's own sleep timer off, because no API can do it there.
  useEffect(() => {
    let lock = null
    let cancelled = false
    const acquire = async () => {
      try {
        if ('wakeLock' in navigator) lock = await navigator.wakeLock.request('screen')
      } catch { /* unsupported or refused; the runbook is the fallback */ }
    }
    acquire()
    const onVisible = () => { if (document.visibilityState === 'visible' && !cancelled) acquire() }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', onVisible)
      lock?.release?.().catch(() => {})
    }
  }, [])

  // A passerby's poke must not leave the wall showing next Thursday forever. Returns to the
  // pinned page rather than the first one where a pin is set — the pin is a deliberate
  // setting, and undoing it is not what "nobody has touched this in five minutes" means.
  const touch = () => {
    clearTimeout(idleTimer.current)
    idleTimer.current = setTimeout(() => {
      setDate(today)
      setView(display?.defaultView || 'day')
      setPage(stickyPage ? stickyPage - 1 : 0)
    }, IDLE_RESET_MS)
  }
  useEffect(() => () => clearTimeout(idleTimer.current), [])

  // Watchdog reload: clears leaks from a month of uptime and picks up new app builds.
  useEffect(() => {
    const hours = display?.reloadHours || 24
    const t = setInterval(() => {
      const h = new Date().getHours()
      if (Date.now() - mountedAt.current > hours * 3600_000 && h >= 2 && h <= 4) {
        window.location.reload()
      }
    }, 600_000)
    return () => clearInterval(t)
  }, [display?.reloadHours])

  const move = (days) => { setDate((d) => addDays(d || today, days)); setPage(0); touch() }

  useEffect(() => {
    // TVs have arrow keys, not mice. Everything here is optional — the screen is complete
    // with zero input.
    const onKey = (e) => {
      if (ctxMenu) {
        if (e.key === 'Escape') setCtxMenu(null)
        return // don't let an arrow key flip the day underneath an open menu
      }
      if (e.key === 'ArrowLeft') move(view === 'week' ? -7 : -1)
      else if (e.key === 'ArrowRight') move(view === 'week' ? 7 : 1)
      else if (e.key === 'PageUp') move(-7)
      else if (e.key === 'PageDown') move(7)
      else if (e.key === 'Enter') { setView((v) => (v === 'day' ? 'week' : 'day')); touch() }
      else if (e.key === 'Home') { setDate(today); setPage(0) }
      else if (e.key === 'f') document.documentElement.requestFullscreen?.().catch(() => {})
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  // Right-click never opens the browser's own menu here — Inspect, Save As and a dozen other
  // things nobody standing in front of a TV should be offered. It opens ours instead, but
  // only when ours has something in it: an empty popover is worse than no popover.
  // `pageCount` is declared below and read at click time, by which point it is set.
  const openMenu = (e) => {
    e.preventDefault()
    if (pageCount > 1) setCtxMenu({ x: e.clientX, y: e.clientY })
  }
  const pickSticky = (n) => {
    chooseStickyPage(n)
    if (n) setPage(n - 1)
    setCtxMenu(null)
  }

  // Auto-rotation through the working week, for an unattended screen.
  useEffect(() => {
    if (!display?.rotateDays || !data?.workingWeek?.length) return
    const t = setInterval(() => {
      const week = data.workingWeek.map((d) => d.date)
      const i = week.indexOf(date)
      setDate(week[(i + 1) % week.length])
    }, (display.rotateSeconds || 20) * 1000)
    return () => clearInterval(t)
  }, [display?.rotateDays, display?.rotateSeconds, data?.workingWeek, date])

  // Pagination by crew, because a TV cannot scroll: a board that overflows does not get a
  // scrollbar, it silently loses the crews at the bottom — who are exactly the people who
  // then don't know where to go.
  //
  // Worked out from measured row heights rather than by trying a page size and seeing
  // whether it fits. That feedback loop oscillated: the page size was global but the
  // measurement was of whichever page happened to be showing, and crews are different
  // sizes, so a page holding a small crew would grow the count, the next page would
  // overflow and shrink it, and the footer flipped between "1 of 5" and "1 of 10" forever.
  // Crew-header height is uniform and so is a day-view row, so measure those once and pack the
  // crews into pages arithmetically — no loop, nothing to settle. A week-view row is the one
  // thing that varies, and it varies by a COUNT the data already knows (cards in its busiest
  // day) times a measured card height, so it stays arithmetic too.
  // The "not on a team" block is dropped here rather than server-side: it is a working
  // affordance — a place to park somebody with no crew so they stay schedulable — and the
  // board still needs it. A wall shows the crews, and a header over a list of people who
  // are on no crew is space a real crew could have used. Filtering at the one place both
  // views paginate from covers the day table and the week grid alike.
  // Memoised because it is a dependency of the packing below, and this screen re-renders on a
  // clock tick: a fresh array every render made the whole pack recompute every 30 seconds for
  // an answer that had not changed.
  const groups = useMemo(
    () => (data?.board?.groups || []).filter((g) => g.kind !== 'unassigned'),
    [data?.board?.groups],
  )
  const [availH, setAvailH] = useState(0)
  // Per view, because the two tables are built from different components with different type
  // scales. `empty` is the day view's extra — a crew with nobody on it draws one short
  // placeholder line, not a name — and `entry` is the week view's: a week cell stacks one card
  // per job, so a row's height depends on the busiest day in it.
  const [metrics, setMetrics] = useState(DEFAULT_METRICS)
  // Whether the board is currently paged, for the hysteresis at the floor.
  const pagedRef = useRef(false)
  const probeRef = useRef(null)

  useEffect(() => {
    const body = bodyRef.current
    if (!body) return undefined
    const read = () => setAvailH(body.clientHeight)
    read()
    // Both, deliberately. ResizeObserver catches the cases a window event does not — the
    // pending-switch banner appearing, the app chrome changing height — but its callbacks
    // are delivered as part of the rendering steps, so a backgrounded or non-compositing
    // tab can sit on them indefinitely. The resize event still arrives there.
    window.addEventListener('resize', read)
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(read) : null
    ro?.observe(body)
    return () => {
      window.removeEventListener('resize', read)
      ro?.disconnect()
    }
  }, [view, isLoading])

  // The constants the packing needs, read off the probe rather than off the board on screen.
  //
  // Measuring the live board is what made the footer flicker. The measurement fed the packing;
  // the packing chose the columns, the scale, and the page count; and all three decided which
  // row `querySelector` reached first and how tall it was — so the measurement's own output
  // became its next input. Rows are not all the same height (an empty crew's placeholder is a
  // short line, a wrapped label is a tall one) and the balanced and paged layouts do not put
  // the same crew first, so that loop had no fixed point: it settled into a two-cycle, flipping
  // between one page and two at frame rate. Silently, because a passive effect resets React's
  // update-depth counter, so nothing was ever logged.
  //
  // Nothing here reads the rendered board any more, so there is no cycle left to damp. The
  // probe is also unscaled, which is why no scale has to be divided back out.
  useEffect(() => {
    const probe = probeRef.current
    if (!probe) return
    const h = (sel) => probe.querySelector(sel)?.getBoundingClientRect().height
    const fallback = DEFAULT_METRICS[view]
    let next

    if (view === 'day') {
      const row = h('.wall-row')
      const crew = h('.wall-crew')
      if (!row || !crew) return
      // The placeholder a crew with nobody on it draws. Measured rather than assumed to equal a
      // staff row, because it is set a step smaller and a crewless crew is common on a Friday.
      const empty = h('.wall-none')
      next = { row, crew, empty: empty || row, head: h('.wall-table thead') ?? fallback.head }
    } else {
      // A row whose busiest day holds one job — the common case, and the only one that is a
      // CONSTANT. Taller rows are that plus a card per extra job, which the packing adds back
      // per row from its own count. The probe holds exactly one such row, so there is no
      // shortest-on-screen to hunt for.
      const row = h('tr.staff-row')
      const crew = h('tr.group-row')
      if (!row || !crew) return
      // A card's own height plus the gap under it. Both are layout values on an untransformed
      // element now, so neither needs correcting.
      //
      // The class is `week-entry`. It was `.entry` here, which WeekGrid has never rendered — so
      // this silently fell through to the 28px default on every measurement, and the stylesheet's
      // matching typo meant the wall's week cards were never restyled for the wall either. Both
      // are fixed; the gap under a card is a real declaration now, so it is measured, not zero.
      const card = probe.querySelector('.week-cell .week-entry')
      const gap = card ? parseFloat(getComputedStyle(card).marginBottom) || 0 : 0
      next = {
        row,
        crew,
        head: h('table.board thead') ?? fallback.head,
        entry: card ? card.getBoundingClientRect().height + gap : fallback.entry,
      }
    }

    // Functional, and returning the same object when nothing moved: that keeps `metrics` out of
    // this effect's dependencies, so it cannot re-trigger itself even if a measurement is noisy.
    setMetrics((m) => {
      const prev = m[view]
      const settled = Object.keys(next).every((k) => Math.abs(prev[k] - next[k]) <= 1)
      return settled ? m : { ...m, [view]: next }
    })
  }, [view, data, availH])

  /**
   * The smallest board that still has one of everything the packing measures: a crew with one
   * person doing one job, and a crew with nobody on it. Rendered hidden and unscaled through the
   * real components, so the constants can never drift from what the board actually draws — the
   * alternative, a hand-written copy of the markup, drifts the first time either table changes.
   *
   * Real days and job types, synthetic people: the column structure has to match (a day table's
   * slot count and a week table's day count both affect the header) while the rows must not
   * depend on the roster, which is the entire point.
   */
  const probeBoard = useMemo(() => {
    const board = data?.board
    if (!board) return null
    const first = board.days?.[0]
    return {
      ...board,
      staff: [{ id: PROBE_ID, name: 'Probe', jobTypeIds: [] }],
      groups: [
        { id: PROBE_ID, name: 'Probe', coordinatorName: 'Probe', staffIds: [PROBE_ID] },
        { id: `${PROBE_ID}-empty`, name: 'Probe', staffIds: [] },
      ],
      assignments: first
        ? [{
            id: PROBE_ID,
            date: first.date,
            renderGroupId: PROBE_ID,
            staffId: PROBE_ID,
            slot: 1,
            label: 'Probe',
          }]
        : [],
      // No time off: an "Off" row is a different shape, and the probe is here for the ordinary
      // one. Left in the roster it would also depend on the date, which the constants must not.
      timeOff: [],
    }
  }, [data?.board])

  const rowsByCrew = useMemo(
    () => (data?.board && date ? crewRows(data.board, date) : new Map()),
    [data?.board, date],
  )

  /**
   * The week view's shape, per crew: one number per row, being the number of job cards in that
   * row's busiest day. That is what sets the row's height, so it is what the packing needs.
   *
   * Mirrors WeekGrid's own two rules deliberately — rows are home members plus anyone borrowed
   * in across the week, and in CAT mode a cell only draws the jobs done FOR that crew — because
   * a height estimate that disagrees with what gets drawn is how a name ends up clipped off the
   * bottom of a page.
   */
  const weekRowsByCrew = useMemo(() => {
    const board = data?.board
    if (!board) return new Map()

    const rowsFor = new Map(board.groups.map((g) => [g.id, [...g.staffIds]]))
    for (const a of board.assignments) {
      const rows = rowsFor.get(a.renderGroupId)
      if (rows && !rows.includes(a.staffId)) rows.push(a.staffId)
    }

    const perDay = new Map() // `${groupId}:${staffId}:${date}` → how many cards land in that cell
    for (const a of board.assignments) {
      const k = `${a.renderGroupId}:${a.staffId}:${a.date}`
      perDay.set(k, (perDay.get(k) || 0) + 1)
    }

    const out = new Map()
    for (const g of board.groups) {
      out.set(
        g.id,
        (rowsFor.get(g.id) || []).map((staffId) => {
          let busiest = 1 // an empty row still occupies one line
          for (const d of board.days) {
            const n = perDay.get(`${g.id}:${staffId}:${d.date}`) || 0
            if (n > busiest) busiest = n
          }
          return busiest
        }),
      )
    }
    return out
  }, [data?.board])

  // Everything on one screen is the goal — a wall board you have to wait on is worse than a
  // wall board you have to walk closer to. So: split the crews across this view's columns, work
  // out what scale that needs, and shrink to it. Only when the whole shop will not fit even at
  // the legibility floor does this fall back to turning pages.
  //
  // All of it is arithmetic on measured constants, never "try a size and see" — that feedback
  // loop is what made the page count oscillate.
  const { scale, pages } = useMemo(() => {
    const cols = COLUMNS_BY_VIEW[view] || 1
    const m = metrics[view]
    const blankPage = () => Array.from({ length: cols }, () => [])
    if (!groups.length) return { scale: 1, pages: [blankPage()] }
    if (!availH) {
      const first = blankPage()
      first[0] = groups
      return { scale: 1, pages: [first] }
    }

    // A day-view row is always one line; a week-view row is one line per job on its busiest
    // day. Same arithmetic either way — measured constants times a count, never "render it
    // and see whether it fit".
    const heightOf =
      view === 'week'
        ? (g) =>
            m.crew +
            (weekRowsByCrew.get(g.id) || [1]).reduce((sum, n) => sum + m.row + (n - 1) * m.entry, 0)
        : (g) => {
            const n = (rowsByCrew.get(g.id) || []).length
            // A crew with nobody on it still draws — as one short placeholder line, which is
            // not a staff row's height. Counting it as one was a small over-estimate per empty
            // crew, and on a quiet day that is most of them.
            return m.crew + (n ? n * m.row : m.empty)
          }
    const heights = groups.map(heightOf)
    const heightByGroup = new Map(groups.map((g, i) => [g, heights[i]]))

    const boardOrder = new Map(groups.map((g, i) => [g, i]))
    const tallestOf = (cs) =>
      Math.max(...cs.map((c) => c.reduce((s, g) => s + heightByGroup.get(g), 0)))

    // What this used to do on its own: walk the crews in order and cut when the running
    // total passes each column's share. It keeps the set order, but it can only cut where
    // the list happens to reach a boundary — so one tall crew landing just past one leaves
    // the other column short by most of a crew.
    const sequential = () => {
      const target = heights.reduce((a, b) => a + b, 0) / cols
      const out = blankPage()
      let col = 0
      let acc = 0
      groups.forEach((g, i) => {
        // Midpoint rule: break when this crew's own middle passes the share, not when its
        // top does. Testing the top overshoots by up to a whole crew.
        if (col < cols - 1 && acc + heights[i] / 2 >= target * (col + 1)) col += 1
        out[col].push(g)
        acc += heights[i]
      })
      return out
    }

    // Tallest crew first into whichever column is currently shortest. Free to put crews in
    // any column, so it is not stuck with where the boundaries happen to fall — on the
    // rosters this is built for it usually balances the columns outright.
    const greedy = () => {
      const out = blankPage()
      const filled = new Array(cols).fill(0)
      for (const g of [...groups].sort((a, b) => heightByGroup.get(b) - heightByGroup.get(a))) {
        let best = 0
        for (let c = 1; c < cols; c += 1) if (filled[c] < filled[best]) best = c
        out[best].push(g)
        filled[best] += heightByGroup.get(g)
      }
      return out
    }

    /**
     * Both, then whichever wins.
     *
     * Greedy beats the sequential split about twenty times as often as it loses, but it is
     * near-optimal rather than optimal, so on the odd roster the old cut lands better. Both
     * are a few sums over a dozen crews, so running the pair and taking the shorter tallest
     * column costs nothing measurable and makes this strictly no worse than what it
     * replaces — the scale is set by that tallest column, so it is the only number worth
     * optimising.
     *
     * Crews stay whole either way; nothing is ever cut across a column boundary. Each
     * column is then re-sorted into board order, which is what preserves the property the
     * sequential version existed for: a column still reads top to bottom in the order the
     * crews are set in, and all that changes is which column a crew lives in.
     */
    const columns = [sequential(), greedy()].reduce((a, b) => (tallestOf(b) < tallestOf(a) ? b : a))
    for (const c of columns) c.sort((a, b) => boardOrder.get(a) - boardOrder.get(b))

    const tallest = tallestOf(columns)
    // A couple of per cent in hand: the estimate is two constants times a row count and
    // does not know about collapsed borders or sub-pixel rounding, and landing 5px over
    // clips a name off the bottom.
    const needed = (tallest + m.head) * 1.02
    const ideal = needed > 0 ? availH / needed : 1

    if (ideal >= 1) {
      pagedRef.current = false
      return { scale: 1, pages: [columns] }
    }
    // Hysteresis, not a bare comparison. A roster that needs almost exactly the floor sits on
    // the boundary, and a sub-pixel wobble either side of it would otherwise add and remove a
    // whole page — which also tears down and restarts the rotation timer below, so the board
    // never actually turns and the crews on page 2 are never shown. Leaving pages behind takes
    // headroom; landing back on the boundary keeps them.
    const floor = pagedRef.current ? MIN_WALL_SCALE * (1 + PAGE_HYSTERESIS) : MIN_WALL_SCALE
    if (ideal >= floor) {
      pagedRef.current = false
      return { scale: ideal, pages: [columns] }
    }
    pagedRef.current = true

    // Too many people for one screen even shrunk as far as is readable. Page it, at that
    // floor, so at least each page is as full as it can be.
    const budget = availH / MIN_WALL_SCALE - m.head
    const out = []
    let page = blankPage()
    let pcol = 0
    let height = 0
    groups.forEach((g, i) => {
      if (height + heights[i] > budget && page[pcol].length) {
        pcol += 1
        height = 0
        if (pcol >= cols) {
          out.push(page)
          page = blankPage()
          pcol = 0
        }
      }
      page[pcol].push(g)
      height += heights[i]
    })
    if (page.some((c) => c.length)) out.push(page)
    return { scale: MIN_WALL_SCALE, pages: out.length ? out : [blankPage()] }
  }, [groups, rowsByCrew, weekRowsByCrew, availH, metrics, view])

  const pageCount = pages.length
  // A pin only means anything while the page it names still exists — a quiet Friday that
  // collapses five pages into two must not leave the wall stuck on a blank page 4.
  const pinnedIndex = stickyPage && stickyPage <= pageCount ? stickyPage - 1 : null

  useEffect(() => {
    if (pinnedIndex !== null) {
      if (page !== pinnedIndex) setPage(pinnedIndex)
      return
    }
    if (page >= pageCount) setPage(0)
  }, [pageCount, page, pinnedIndex])

  // Both views turn their own pages. The week view used to be exempt because it did not have
  // pages — it just overflowed the panel and the crews past the bottom edge were gone.
  useEffect(() => {
    if (pageCount <= 1 || pinnedIndex !== null) return
    const t = setInterval(() => setPage((p) => (p + 1) % pageCount), (display?.pageSeconds || 15) * 1000)
    return () => clearInterval(t)
  }, [pageCount, display?.pageSeconds, pinnedIndex])

  if (error && !data) {
    return (
      <div className="wb wb-message">
        <h1>Not signed in</h1>
        <p>Open <code>/tv</code> on this screen and enter a pairing code from the owner.</p>
      </div>
    )
  }
  // Branded even before the board arrives. A wall screen coming up after a power cut spends
  // its first seconds in front of a whole shop, and a bare "Loading…" looks like something
  // broken rather than something starting. Its own tiny request, because the
  // whiteboard payload is the slow one and this only needs a name and a logo.
  if (isLoading || !data) {
    return (
      <div className="wb wb-message">
        {branding?.logoPath && (
          <img className="wb-boot-logo" src={`/uploads/${branding.logoPath}`} alt="" />
        )}
        <h1>{branding?.name || 'CrewDispatch Service'}</h1>
        <p className="wb-boot-note">Loading the board…</p>
      </div>
    )
  }

  const dimmed = isDimmed(now, display)
  const board = data.board
  const columns = pages[page] || pages[0]

  const stale = Date.now() - dataUpdatedAt > REFRESH_MS * 3
  // A TV is read-only, so it shows the badge and never the control that changes it.
  const dayHoliday = data.board?.days?.find((x) => x.date === (date || today))?.holiday || null

  return (
    <div
      className={`wb ${dimmed ? 'dimmed' : ''} ${data.mode === 'cat' ? 'cat' : ''}`}
      style={{
        // Overscan: many panels crop 2–5% of the frame, and tuning it from the office beats
        // tuning it from a ladder. The TOP edge is exempt and takes a hair's breadth instead:
        // the header there is one short line, the board below it is what the screen is for,
        // and a percentage of a 4K panel's height was costing most of a crew before anything
        // had been drawn. The other three edges keep the full allowance, which is where a
        // panel's crop actually tends to bite.
        padding: `5px ${display?.safeAreaPct ?? 2.5}% ${display?.safeAreaPct ?? 2.5}%`,
        fontSize: `${(display?.scalePct ?? 100) / 100}rem`,
      }}
      onMouseMove={touch}
      onTouchStart={touch}
      onContextMenu={openMenu}
    >
      {/* The day selector is navigation, and a wall display is not navigated — it is read.
          It collapses to nothing and reveals itself when the pointer comes anywhere near the
          top of the screen — the whole header plus a strip right under it, not just a thin
          sliver you have to hunt for below a gap. */}
      <div className="wb-nav-zone">
        <header className="wb-head">
          {data.branding?.logoPath && <img src={`/uploads/${data.branding.logoPath}`} alt="" />}
          <div className="wb-title">
            <h1>{formatDate(date || today, { withYear: true })}</h1>
            {(data.mode === 'cat' || data.isFiltered || dayHoliday?.observed) && (
              <div className="wb-sub">
                {dayHoliday?.observed && <span className="wb-stat">{dayHoliday.name}</span>}
                {data.mode === 'cat' && (
                  <span className="wb-mode">
                    {/* From the payload, not useAuth: a paired TV has no session to read the
                        tenant's vocabulary from. */}
                    {nameOf(data.modes, 'cat')}{data.modeLabel ? ` · ${data.modeLabel}` : ''}
                  </span>
                )}
                {data.isFiltered && <span className="wb-filtered">Filtered view — not the whole company</span>}
              </div>
            )}
          </div>
          <div className="wb-clock">
            <div className={`wb-time ${stale ? 'stale' : ''}`}>
              {now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        </header>

        <div className="wb-week-zone">
          <nav className="wb-week">
            {/* Arrows, because the strip only ever showed the CURRENT working week: reaching
                next Tuesday was possible with a keyboard nobody has plugged into a TV, and
                impossible with the mouse somebody actually walked over with. */}
            <button className="wb-nav-btn" onClick={() => move(-1)} aria-label="Previous day">‹</button>

            <div className="wb-days">
              {data.workingWeek.map((d) => (
                <button
                  key={d.date}
                  className={`wb-day ${d.date === date ? 'selected' : ''} ${
                    d.date === today ? 'today' : ''
                  } ${d.holiday?.observed ? 'stat' : ''}`}
                  onClick={() => { setDate(d.date); setPage(0); touch() }}
                >
                  <span className="wd">{weekdayName(d.date)}</span>
                  <span className="dt">{formatShort(d.date)}</span>
                  {/* Only when there is something on: a column of zeroes is noise, and the
                      absence of a number already says "nothing booked". */}
                  {d.count > 0 && <span className="ct">{d.count}</span>}
                  {d.holiday?.observed && <span className="st">STAT</span>}
                </button>
              ))}
            </div>

            <button className="wb-nav-btn" onClick={() => move(1)} aria-label="Next day">›</button>

            <span className="wb-spacer" />

            <button
              className="wb-pill"
              onClick={() => { setDate(today); setPage(0); touch() }}
              disabled={date === today}
            >
              Today
            </button>
            <button className="wb-pill" onClick={() => { setView(view === 'day' ? 'week' : 'day'); touch() }}>
              {view === 'day' ? 'Week' : 'Day'}
            </button>
          </nav>
        </div>
      </div>

      {/* Both views go through the same scale-then-page treatment. Scaled with a transform
          rather than a font-size, so every measurement, padding and border shrinks in
          proportion — a font-size alone would leave the rem-based spacing behind and the rows
          would stop being evenly split.

          Keyed by page so React remounts on a turn and the slide-in replays: the point of
          animating it is that someone glancing up notices the board changed rather than
          silently reading the wrong crew's Monday. */}
      <main className="wb-body" ref={bodyRef}>
        <div
          className="wall-scaler"
          style={{ transform: `scale(${scale})`, width: `${100 / scale}%` }}
        >
          <div
            className="wall-columns"
            key={`${view}-${page}`}
            style={{ '--wall-cols': String(columns.length) }}
          >
            {columns.map((colGroups, i) =>
              view === 'day' ? (
                <WallDay key={i} board={{ ...board, groups: colGroups }} date={date} />
              ) : (
                // Read-only: no onPickDay, no onAddTimeOff. The week grid takes both and this
                // screen passes neither, which is what keeps the wall a display and not a
                // control surface somebody can lean on.
                <WeekGrid key={i} board={{ ...board, groups: colGroups }} readOnly />
              ),
            )}
          </div>
        </div>

        {/* The measurement probe. Hidden, out of flow, unscaled, and built from a board that
            does not depend on the roster, the date or the packing — which is what makes the
            row heights it yields constants rather than a reading of whatever is on screen.
            Rendered through the same components as the board above so the two cannot drift. */}
        {probeBoard && (
          <div className="wall-probe" aria-hidden="true" ref={probeRef}>
            {view === 'day' ? (
              <WallDay board={probeBoard} date={probeBoard.days?.[0]?.date} />
            ) : (
              <WeekGrid board={probeBoard} readOnly />
            )}
          </div>
        )}
      </main>

      {pageCount > 1 && (
        <footer className="wb-foot">
          {pages.map((_, i) => (
            <span key={i} className={`wb-pip ${i === page ? 'on' : ''}`} />
          ))}
          {/* So it is obvious from across the room why the board stopped moving, rather
              than looking like it has frozen. */}
          {pinnedIndex !== null && (
            <span className="wb-pinned" title={`Stuck on page ${pinnedIndex + 1}`}>
              <Icon name="pin" size={12} strokeWidth={2} />
            </span>
          )}
        </footer>
      )}

      {ctxMenu && (
        <ContextMenu x={ctxMenu.x} y={ctxMenu.y} onClose={() => setCtxMenu(null)}>
          {/* The one thing worth offering on a wall display: which page it stops on. The
              pages are listed rather than typed into — this screen is operated with a remote
              or a fingertip, and a number field is the one control neither can manage. */}
          {stickyPage ? (
            <ContextMenuItem icon="close" onClick={() => pickSticky(null)}>
              Disable sticky page
            </ContextMenuItem>
          ) : (
            <>
              <ContextMenuLabel>Sticky page</ContextMenuLabel>
              {pages.map((_, i) => (
                <ContextMenuItem key={i} icon="pin" onClick={() => pickSticky(i + 1)}>
                  Page {i + 1}
                </ContextMenuItem>
              ))}
            </>
          )}
        </ContextMenu>
      )}
    </div>
  )
}

function isDimmed(now, display) {
  if (!display?.dimStart || !display?.dimEnd) return false
  const hhmm = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
  const start = display.dimStart.slice(0, 5)
  const end = display.dimEnd.slice(0, 5)
  return start <= end ? hhmm >= start && hhmm < end : hhmm >= start || hhmm < end
}
